import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Copy } from "lucide-react";
import { useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { toast } from "sonner";
import { CpyRequest, useCpyCopyMutation } from "@/services/backend/cpy/api/cpy";
import { CpyOptions } from "./cpy-options";
import { CpyName } from "../types/entity-names";

export function CpySourceDialog({
  destinationEntityName,
  destinationId,
  sourceEntityName,
  sourceId,
  onSuccess,
}: {
  destinationEntityName: CpyName;
  destinationId: string;
  sourceEntityName: CpyName;
  sourceId: string;
  onSuccess?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CpyRequest>({
    destinationEntityName,
    destinationId,
    sourceEntityName,
    sourceId,
  });
  const [patch, { isLoading, error, reset }] = useCpyCopyMutation();

  const doCopy = () => {
    if (isLoading) return;

    patch({ ...request })
      .unwrap()
      .then(() => {
        reset();
        setOpen(false);
        onSuccess?.();
        toast.success(t("Daten kopiert"));
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="cursor-pointer">
          <Copy />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Daten wirklich kopieren?")}</DialogTitle>
          <DialogDescription>
            {t("Durch bestätigen werden die Daten unwiederruflich übernommen.")}
          </DialogDescription>
        </DialogHeader>
        <CpyOptions
          destinationEntityName={destinationEntityName}
          sourceEntityName={sourceEntityName}
          options={request.options}
          onOptionsChange={(options) => setRequest({ ...request, options })}
        />
        <RTKQueryErrorAlert error={error} showValidationErrors />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doCopy} disabled={isLoading}>
            {t("Bestätigen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
